import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { apiGet } from '../../../lib/api.js';
import { resolveLook } from '../../../lib/bookingSlots.js';
import BookingGrid from '../../../components/booking/BookingGrid.jsx';

export const PREVIEW_QUERY_KEY = 'booking-page-preview';

/**
 * Live preview of the public booking page. It renders the SAVED availability
 * (same endpoint as /book/:slug) and is re-fetched whenever a save invalidates
 * PREVIEW_QUERY_KEY, so it never shows stale slots.
 * @param {{slug: string, appearance: object, logoUrl?: string, title?: string, instructions?: string}} props
 * @returns {JSX.Element}
 */
export default function BookingPreviewCard({ slug, appearance, logoUrl, title, instructions }) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [PREVIEW_QUERY_KEY, slug],
    queryFn: () => apiGet(`/booking/page/${encodeURIComponent(slug)}`),
    enabled: !!slug,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  // CIVIX dark/purple unless the agent picked their own colours.
  const look = resolveLook(appearance, { dark: true });
  const tz = data?.page?.timezone;

  return (
    <div className="lg:col-span-6 space-y-2" data-testid="page-preview">
      <div className="text-center italic text-xs font-semibold text-white/50">
        Booking page preview{isFetching && !isLoading ? ' · updating…' : ''}
      </div>
      <div className="rounded-xl p-4 shadow-lg border border-civix-purple/30" style={{ backgroundColor: look.backgroundColor, color: look.fontColor }}>
        <div className="flex justify-center mb-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-md" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white shadow-md shadow-purple-500/30" style={{ backgroundColor: look.slotColor }}>
              <Calendar className="w-5 h-5" />
            </div>
          )}
        </div>
        <h4 className="text-sm font-bold text-center leading-snug px-2 mb-0.5">
          {title || 'Please select a time slot that would best fit your schedule.'}
        </h4>
        <p className="text-[11px] text-center opacity-70 mb-3">{instructions || 'Appts typically last between 5-10 minutes'}</p>

        {!slug ? (
          <div className="py-6 text-center text-sm opacity-70">Save your calendar to preview your live availability.</div>
        ) : isLoading ? (
          <div className="py-6 text-center text-sm opacity-70">Loading availability…</div>
        ) : (
          <BookingGrid slots={data?.slots || []} timeZone={tz} appearance={look} stepMinutes={data?.page?.durationMinutes || 30} daysOut={data?.page?.maxBookingDaysOut || 7} compact />
        )}
      </div>
    </div>
  );
}
